import clsx from "clsx"
import L from "leaflet"
import { memo, useMemo } from "react"
import { MarkerProps, Tooltip } from "react-leaflet"
import { batch, shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { selectDriverById } from "@/store/drivers"
import { setFocusedDriver, setFocusedOrder, setShowDriverDetails } from "@/store/ui"
import { Driver, RootState } from "@/types"
import { DriverUtils, usePrevious } from "@/utils"

import { DriverPopup } from "../Popup/DriverPopup"
import RotatedMarker from "./RotatedMarker"

export const DriversMarkers = ({
  selector,
  zIndexOffset,
}: {
  selector: (state: RootState) => Array<Driver["id"]>
  zIndexOffset: number
}) => {
  const drivers = useAppSelector(selector, shallowEqual)

  return (
    <>
      {drivers.map((driverId) => (
        <DriverMarker driverId={driverId} key={`driver-marker-${driverId}`} zIndexOffset={zIndexOffset} />
      ))}
    </>
  )
}

type DriverMarkerProps = {
  driverId: Driver["id"]
  zIndexOffset?: MarkerProps["zIndexOffset"]
}

export const DriverMarker = memo(({ driverId, zIndexOffset }: DriverMarkerProps) => {
  const driver = useAppSelector(selectDriverById(driverId), shallowEqual)

  const isFocused = useAppSelector((state) => state.ui.focusedDriverId === driverId)

  const dispatch = useDispatch()

  const icon = useMemo(() => getMarkerIcon(driver?.state || "offline", isFocused), [driver?.state, isFocused])

  const position = useMemo(() => [driver?.lat, driver?.lng] as number[], [driver?.lat, driver?.lng])
  const previousPosition = usePrevious(position)

  if (!driver || !driver.lat || !driver.lng) return null

  let rotationAngle = driver.heading || 0

  const hasPositionChanged = !shallowEqual(position, previousPosition)
  if (previousPosition && hasPositionChanged) {
    const deltaLat = position[0] - previousPosition[0]
    const deltaLng = position[1] - previousPosition[1]

    rotationAngle = (Math.atan2(deltaLng, deltaLat) * 180) / Math.PI
  }

  return (
    <RotatedMarker
      // @ts-expect-error: Rotated marker is not typed; remove after upgrading to react-leaflet v3
      icon={icon}
      position={position}
      rotationOrigin="center center"
      rotationAngle={rotationAngle}
      zIndexOffset={zIndexOffset}
      onClick={() => {
        batch(() => {
          dispatch(setFocusedDriver(driverId))
          dispatch(setFocusedOrder(null))
          dispatch(setShowDriverDetails(false))
        })
      }}
    >
      <DriverPopup driverId={driverId} />
      <Tooltip className="custom-leaflet-tooltip" direction="top" offset={[0, -12]} opacity={1}>
        {driver.name}
      </Tooltip>
    </RotatedMarker>
  )
}, shallowEqual)

function getMarkerIcon(state: Driver["state"], isFocused = false) {
  const stateColor = DriverUtils.getStateColor(state)

  return L.divIcon({
    className: clsx("driver-marker", stateColor, isFocused && "focused"),
    iconAnchor: [8, 8],
    popupAnchor: [0, -16],
    iconSize: [16, 16],
  })
}
